import { Box, IconButton, MenuItem, TextField, Tooltip } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import RefreshIcon from "@mui/icons-material/Refresh";
import type { Moment } from "moment";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import type { CostCenterOption } from "../models/FirstTurnedRolls";

interface Props {
	start: Moment;
	end: Moment;
	onStartChange: (date: Moment | null) => void;
	onEndChange: (date: Moment | null) => void;
	costCenterId: number | null;
	onCostCenterChange: (id: number | null) => void;
	costCenters: CostCenterOption[];
	onRefresh: () => void;
	onExportExcel: () => void;
	isLoading: boolean;
}

export const FirstTurnedRollsToolbar = ({
	start,
	end,
	onStartChange,
	onEndChange,
	costCenterId,
	onCostCenterChange,
	costCenters,
	onRefresh,
	onExportExcel,
	isLoading,
}: Props) => {
	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				px: 1.5,
				py: 0.75,
				flexWrap: "wrap",
			}}
		>
			<Box sx={{ width: 240 }}>
				<TextField
					select
					size="small"
					label="Cost Center"
					fullWidth
					value={costCenterId == null ? "" : String(costCenterId)}
					onChange={(e) => {
						const v = e.target.value;
						onCostCenterChange(v === "" ? null : Number(v));
					}}
					slotProps={{
						select: { displayEmpty: true },
						inputLabel: { shrink: true },
					}}
				>
					{/* Placeholder matching legacy: the page opens with no cost
					    center chosen and an empty grid until the user picks one. */}
					<MenuItem value="" disabled>
						Choose...
					</MenuItem>
					{costCenters.map((cc) => (
						<MenuItem key={cc.Id} value={String(cc.Id)}>
							{cc.Name}
						</MenuItem>
					))}
				</TextField>
			</Box>

			<Box sx={{ minWidth: 150, maxWidth: 170 }}>
				<DatePicker
					label="Start"
					format="MM/DD/YYYY"
					value={start}
					maxDate={end}
					onChange={onStartChange}
					slotProps={{ textField: { size: "small", fullWidth: true } }}
				/>
			</Box>
			<Box sx={{ minWidth: 150, maxWidth: 170 }}>
				<DatePicker
					label="Close"
					format="MM/DD/YYYY"
					value={end}
					minDate={start}
					onChange={onEndChange}
					slotProps={{ textField: { size: "small", fullWidth: true } }}
				/>
			</Box>

			<Tooltip title="Refresh">
				<span>
					<IconButton
						onClick={onRefresh}
						disabled={isLoading}
						size="small"
						sx={{
							bgcolor: "primary.main",
							color: "white",
							width: 28,
							height: 28,
							"&:hover": { bgcolor: "primary.dark" },
							"&.Mui-disabled": {
								bgcolor: "action.disabledBackground",
								color: "action.disabled",
							},
						}}
					>
						<RefreshIcon sx={{ fontSize: 18 }} />
					</IconButton>
				</span>
			</Tooltip>

			<Box sx={{ flexGrow: 1, minWidth: 8 }} />

			<Box sx={{ width: 200 }}>
				<ExcelButton handleExportToExcel={onExportExcel} />
			</Box>
		</Box>
	);
};
